import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  CardHeader, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Divider, 
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import { 
  Description as DocumentIcon, 
  FileCopy as TemplateIcon, 
  Notifications as NotificationIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as RecentIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { documentAPI, templateAPI } from '../services/api';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentDocuments, setRecentDocuments] = useState<any[]>([]);
  const [recentTemplates, setRecentTemplates] = useState<any[]>([]);
  const [activityFeed, setActivityFeed] = useState<any[]>([]);
  
  // Mock analytics data
  const analyticsData = [
    { name: 'Jan', documents: 4, templates: 2 },
    { name: 'Feb', documents: 7, templates: 3 },
    { name: 'Mar', documents: 5, templates: 1 },
    { name: 'Apr', documents: 10, templates: 4 },
    { name: 'May', documents: 8, templates: 2 },
    { name: 'Jun', documents: 12, templates: 5 },
  ];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch recent documents
        const documentsResponse = await documentAPI.getAll();
        setRecentDocuments(documentsResponse.data.slice(0, 5));
        
        // Fetch recent templates
        const templatesResponse = await templateAPI.getAll();
        setRecentTemplates(templatesResponse.data.slice(0, 5));
        
        // Mock activity feed data
        setActivityFeed([
          { id: 1, type: 'document', action: 'created', title: 'Employment Agreement', timestamp: '2 hours ago' },
          { id: 2, type: 'template', action: 'edited', title: 'NDA Template', timestamp: '1 day ago' },
          { id: 3, type: 'document', action: 'shared', title: 'Service Agreement', timestamp: '3 days ago' },
          { id: 4, type: 'template', action: 'created', title: 'Privacy Policy', timestamp: '1 week ago' },
        ]);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Use mock data on error
        handleAPIError();
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Handle errors gracefully
  const handleAPIError = () => {
    // For demo purposes, we'll use mock data if the API calls fail
    setRecentDocuments([
      { id: '1', title: 'Employment Contract', updatedAt: '2023-05-20T10:30:00Z' },
      { id: '2', title: 'Non-Disclosure Agreement', updatedAt: '2023-05-18T14:20:00Z' },
      { id: '3', title: 'Service Agreement', updatedAt: '2023-05-15T09:45:00Z' },
    ]);
    
    setRecentTemplates([
      { id: '1', name: 'NDA Template', category: 'Confidentiality' },
      { id: '2', name: 'Employment Contract', category: 'Employment' },
      { id: '3', name: 'Service Agreement', category: 'Services' },
    ]);
    
    setLoading(false);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Simple chart component using HTML/CSS instead of recharts
  const renderSimpleChart = () => {
    const maxValue = Math.max(...analyticsData.map(item => Math.max(item.documents, item.templates)));
    
    return (
      <Box sx={{ width: '100%', height: 300, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          {analyticsData.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${100 / analyticsData.length}%` }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: 220, width: '100%', justifyContent: 'flex-end' }}>
                <Box 
                  sx={{ 
                    width: '45%', 
                    height: `${(item.documents / maxValue) * 100}%`, 
                    backgroundColor: '#2196f3',
                    ml: 'auto',
                    mr: 0.5
                  }} 
                />
                <Box 
                  sx={{ 
                    width: '45%', 
                    height: `${(item.templates / maxValue) * 100}%`, 
                    backgroundColor: '#4caf50',
                    ml: 0.5,
                    mr: 'auto'
                  }} 
                />
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>{item.name}</Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#2196f3', mr: 1 }} />
            <Typography variant="body2">Documents</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: '#4caf50', mr: 1 }} />
            <Typography variant="body2">Templates</Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  
  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>Dashboard</Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Create, edit, and manage your contracts with AI-powered tools.
        </Typography>
      </Box>
      
      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Quick Actions</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Upload Document</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Upload an existing contract to edit and enhance it with AI.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={() => navigate('/documents/upload')}
                >
                  Upload Document
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <AutoAwesomeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Generate with AI</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Describe what you need and let AI generate a contract for you.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={() => navigate('/documents/upload?tab=ai')}
                >
                  Generate with AI
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <TemplateIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Use Template</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Start from a pre-built template for common contract types.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={() => navigate('/templates')}
                >
                  Browse Templates
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      <Grid container spacing={3}>
        {/* Analytics Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Document Activity
            </Typography>
            {renderSimpleChart()}
          </Paper>
        </Grid>
        
        {/* Recent Documents */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Recent Documents" 
              action={
                <Button size="small" onClick={() => navigate('/documents')}>
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <List>
                {recentDocuments.length > 0 ? (
                  recentDocuments.map((doc) => (
                    <React.Fragment key={doc.id}>
                      <ListItem 
                        button 
                        onClick={() => navigate(`/documents/${doc.id}`)}
                      >
                        <ListItemIcon>
                          <DocumentIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={doc.title} 
                          secondary={new Date(doc.updatedAt).toLocaleDateString()}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No documents found" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recent Templates */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader 
              title="Recent Templates" 
              action={
                <Button size="small" onClick={() => navigate('/templates')}>
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <List>
                {recentTemplates.length > 0 ? (
                  recentTemplates.map((template) => (
                    <React.Fragment key={template.id}>
                      <ListItem 
                        button 
                        onClick={() => navigate(`/templates/${template.id}`)}
                      >
                        <ListItemIcon>
                          <TemplateIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary={template.name} 
                          secondary={template.category}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText primary="No templates found" />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Activity Feed */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Recent Activity" />
            <Divider />
            <CardContent>
              <List>
                {activityFeed.map((activity) => (
                  <React.Fragment key={activity.id}>
                    <ListItem>
                      <ListItemIcon>
                        {activity.type === 'document' ? <DocumentIcon /> : <TemplateIcon />}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <>
                            <Typography component="span" sx={{ fontWeight: 'bold' }}>
                              {activity.title}
                            </Typography>
                            {' was '}
                            {activity.action}
                          </>
                        } 
                        secondary={activity.timestamp}
                      />
                      <Chip 
                        size="small" 
                        label={activity.type} 
                        color={activity.type === 'document' ? 'primary' : 'secondary'} 
                        variant="outlined" 
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage; 